import { Injectable } from '@angular/core';

import {
  DocumentUpdater,
  SharedUrlRoutes,
  UtilDocument,
  UtilDocumentId,
  UtilError,
  UtilHttpStatus,
  UtilType
} from '@formbird/shared';
import * as Handlebars from 'handlebars';

import { LoggedInUserService } from '../user/logged-in-user.service';
import { IndexedDBConnectorService } from '../indexeddb/indexed-dbconnector.service';
import { UtilService } from '../../utils/UtilService';
import { RestDataService } from './rest-data.service';
import { OfflineStatusService } from '../offline-status/offline-status.service';
import { IndexedDBService } from '../indexeddb/indexed-db.service';
import { ClientAccessService } from '../access/client-access.service';
import { Observable } from 'rxjs';
import { User, SharedConstants } from '@formbird/types';
import { select } from '../../redux/decorators/select';
import { DocumentSessionService } from '../document/document-session.service';

const serverRoutes = SharedUrlRoutes.serverRoutes;

const logger = console;

@Injectable({
  providedIn: 'root'
})
export class DataService {
  // the preferred service will be called before the secondary service. So if caching is enabled the preferred service will be
  // the IndexedDBService. If caching is not enabled the preferred service will be the rest data service
  preferredService: any;
  secondaryService: any;

  @select(['userState', 'user']) user$: Observable<User>;
  user: User;
  isRunningPreviousSync: boolean;

  constructor(
    private indexedDBConnectorService: IndexedDBConnectorService,
    private loggedInUserService: LoggedInUserService,
    private restDataService: RestDataService,
    private offlineStatusService: OfflineStatusService,
    private indexedDBService: IndexedDBService,
    private clientAccessService: ClientAccessService,
    private documentSessionService: DocumentSessionService
  ) {
    if (this.user$){
      this.user$.subscribe((data: User) => this.user = data);
    }
  }

  //
  // // the preferred service. This can be configured to either check the cache for data first or go to the
  // // network first. The service to call first will be set based on the ConfigService.clientConfig.preferCachedData value
  // var preferredService = null;
  //
  // // the secondaryService will be tried if the preferred service fails
  // var secondaryService = null;

  ////////////////////////////////////////
  // local service functions

  /**
   * cache a document for offline use
   * @param docInfo - the document used to load full document to cache
   * @param versions - the versions used to cache
   */
  cacheDocument(docInfo, versions): Promise<any> {

    const _self = this;

    if (_self.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {

      if (!versions || !versions.length) {

        return _self.restDataService.loadVersions(docInfo.versionIds).then(
          (versions) => {

            return _self.indexedDBService.cachePushedDocument(docInfo, versions);

          },
          (error) => {

            return Promise.reject(error);

          }
        );
      }

      return _self.indexedDBService.cachePushedDocument(docInfo, versions);

    }

    return Promise.resolve(versions);
  }

  async documentExists(documentId: string): Promise<any> {

    if (!documentId) {
      throw new Error('No documentId specified for check exist!');
    }

    const offlineMode = this.loggedInUserService.getUserConfigItem('cachingEnabled') === true;

    try {

      return offlineMode ? await this.checkDocumentExistsOfflineMode(documentId) : await this.checkDocumentExistFromServer(documentId);

    } catch (err) {
      throw UtilError.createErrorObject(err);
    }
  }

  /**
   * get a document
   */
  async getDocument(id: string): Promise<any> {

    if (!id) {
      // old code reject this error as string
      // tslint:disable-next-line:no-string-throw
      throw 'No documentId specified for getDocument!';
    }

    try {
      const offlineMode = this.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
      const doc = offlineMode ? await this.getDocumentOfflineMode(id) : await this.getDocumentFromServer(id);

      if (doc.systemHeader && doc.systemHeader.offlineDetails) {
        delete doc.systemHeader.offlineDetails;
      }

      return doc;

    } catch (err) {

      let error;

      if (err.status === UtilHttpStatus.FORBIDDEN) {
        error = UtilError.createError(err.message, UtilError.FORBIDDEN_ERROR);
      } else if (err.status === UtilHttpStatus.NOT_FOUND) {
        error = UtilError.createError(err.message, UtilError.NOT_FOUND_ERROR);
      } else if (err.status === UtilHttpStatus.GONE) {
        error = UtilError.createError(err.message, UtilError.DELETED_ERROR);
      } else {
        error = UtilError.createErrorObject(err);
      }

      throw error;
    }
  }

  /* if offline mode then retrieve the latest version of the document from offline db and name as cachedDoc
   * if the cachedDoc is not yet sent to server then return it;
   * else query the server for the latest versionId of the document
   * if versions don't match, use server's version and perform 'documentReset'
   *
   * DocumentReset will remove all versions of a document
   * and start from the beginning as a single version (that current version from the server)
   *
   * DocumentReset is there primarily there to simplify retrieving documents once there have
   * been version conflicts that have been resolved by the server: when two users modifies
   * a single document at the same time offline. If this do not occur, then documentReset will not happen.
   */
  getDocumentOfflineMode(id): Promise<any> {

    const _self = this;

    return new Promise((resolve, reject) => {

      _self.indexedDBService.getDocumentCurrentVersion(id,
        (cacheDoc) => { // latest version

          if (cacheDoc == null) { // not found in offline? retrieve from server
            _self.getDocumentFromServer(id).then(resolve, reject);
          } else {
            resolve(cacheDoc);
          }

          // Do not retrieve the server's version if this document is still unsent.
          // var unsent = cacheDoc.offlineDetails &&
          //     cacheDoc.offlineDetails.status === IndexedDBConstants.OFFLINE_STATUS_PENDING;
          // if (unsent) {
          //     deferred.resolve(cachedObject);
          //     return;
          // }


          // //let's see if we have conflicts from the server:
          // getDocumentFromServer(id).then((doc) => {
          //     var identicalDocuments = doc.systemHeader.versionId === cachedDoc.systemHeader.versionId;
          //     if (!identicalDocuments) {//version conflict! see 10049 & 10539
          //         IndexedDBService.documentReset(doc.documentId);
          //     //doc here is the latest version from the document that have been resolved by the server from conflict
          //         cacheDocument(doc);
          //     }
          //
          //     deferred.resolve(doc);
          //
          // }, function err(statusCode){
          //     //we could not retrieve from the server due to some error, return the cachedDoc instead
          //     deferred.resolve(cachedDoc);
          //
          // });

        }
      );
    });
  }

  async getDocumentFromServer(id: string): Promise<any> {
    try {
      return await this.restDataService.getDocument(serverRoutes.document, id);
    } catch (error) {
      const offlineMode = this.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
      if (offlineMode && error && error.status === UtilHttpStatus.SERVER_UNRESPONSIVE) {
        error.status = UtilHttpStatus.NOT_FOUND;
      }

      throw error;
    }
  }

  private async checkDocumentExistsOfflineMode(id: string) {

    const _self = this;

    let found: boolean = await new Promise((resolve) => {

      _self.indexedDBService.getDocumentCurrentVersion(id, cacheDoc => {
        resolve(!!cacheDoc);
      });

    });

    try {
      if (!found) {
        found = !!(await _self.checkDocumentExistFromServer(id));
      }
    } catch (err) {
      throw err;
    }

    return found;
  }

  private async checkDocumentExistFromServer(id: string) {

    const _self = this;

    try {
      return await _self.restDataService.checkDocumentExists(serverRoutes.documentExists, id);
    } catch (error) {
      const offlineMode: boolean = _self.loggedInUserService.getUserConfigItem('cachingEnabled') === true;

      if (offlineMode && error && error.status === UtilHttpStatus.SERVER_UNRESPONSIVE) {
        error.status = UtilHttpStatus.NOT_FOUND;
        error.message = 'Network connection lost. Could not retrieve.';
      }

      throw error;
    }
  }

  findByElastic(query, searchContext?): Promise<any> {
    const options = {
      filter: query
    };

    return this.search(options, searchContext);
  }

  search(options, searchContext): Promise<any> {

    const _self = this;

    return new Promise((resolve, reject) => {
      if (searchContext) {
        const keys = Object.keys(searchContext);
        if (keys && keys.length) {
          for (const key of keys) {
            options[key] = searchContext[key];
          }
        }
      }

      const offlineMode = _self.loggedInUserService.getUserConfigItem('cachingEnabled') === true;

      const searchOffline = () => {
        _self.indexedDBService.search(options).then(
          (data2) => {
            resolve(data2);
          },
          (err) => {
            const msg = 'Search by elastic failed with error: ' + err;
            logger.error(msg);
            reject(new Error(msg));
          }
        );
      };

      if (options.text || options.filter) {
        if (options.document) {
          const doc = {
            document: options.document,
            account: _self.user.account
          };

          if (!UtilType.isString(options.filter)) {
            options.filter = JSON.stringify(options.filter);
          }

          const hbTemplate = Handlebars.compile(options.filter);
          options.filter = hbTemplate(doc);

          delete options.document;
        }

        if (options.searchOfflineOnly) {
          if (!offlineMode) {
            reject(new Error('Please enable caching before performing a search configured as offline only'));
          } else {
            searchOffline();
          }
        } else {
          _self.restDataService.search(options).then(
            (data: any) => {

              if (!data.statusCode && data.statusCode !== UtilHttpStatus.OK && offlineMode) {
                searchOffline();
              } else {
                resolve(data);
              }

            }, (err) => {

              if (offlineMode) {
                searchOffline();
              } else {
                reject(err);
              }
            }
          );
        }

      } else {

        const er = UtilError.createError('Undefined search text or query!');
        reject(er);

      }
    });
  }

  /** initialise the service passed in and return a promise for initialisation completion
   *
   * @param service - the service to initialise
   */
  initialiseService(service) {
    if (service && service.initialise) {
      return service.initialise();
    }

    // no initialise function in service
    return Promise.resolve();
  }

  terminateService(service): Promise<any> {
    if (service && service.terminateService) {
      return service.terminateService();
    }

    // no terminateService function in service
    return Promise.resolve();
  }

  /** set the versionId and previousVersionId fields */
  setVersionFields(document) {
    if (document && document.systemHeader) {
      if (document && document.systemHeader.versionId) {
        document.systemHeader.previousVersionId = document.systemHeader.versionId;

        document.systemHeader.versionId = UtilDocumentId.generateId();

        // update createdBy every time because when we save the doc it's a new version
        document.systemHeader.createdBy = this.user ?
          this.user.account.documentId : null;
      }
    }
  }

  private hasOfflineAccess(document) {
    return this.clientAccessService.hasPermission(document, SharedConstants.OPERATION_TYPE_OFFLINE);
  }

  private isAccountDoc(doc) {
    return doc.systemHeader && (
      doc.systemHeader.systemType === SharedConstants.SYSTEM_TYPE_ACCOUNT ||
      doc.systemHeader.systemType === SharedConstants.SYSTEM_TYPE_ACCOUNT_SECURITY ||
      doc.systemHeader.systemType === SharedConstants.SYSTEM_TYPE_ACCOUNT_CONTROL);
  }

  async jsonPatchUpdate(originalBean, newBean, updateOption?) {
    // set the details like the versionId and previousVersionId
    this.setVersionFields(newBean);

    // set the systemHeader.createdDate in the new version. The systemHeader.createdDate is the created date of the
    // version, rather than the document, so each version needs to have a new createdDate. See Mantis 10816 for further
    // details
    newBean.systemHeader.createdDate = Date.now();

    // always use the Database Service if caching is enabled, so that the items will be placed in the
    // queue to be sent to the server, otherwise items can be saved in the wrong
    // order if there is anything already in the queue
    const updates = await DocumentUpdater.getUpdates(originalBean, newBean);
    const options: any = {
      updates: updates,
      isUpdateFromPreviousVersion: UtilDocument.isPreviousVersion(newBean),
      template: updateOption ? updateOption.templateId : null,
      makeCurrentVersion: updateOption ? updateOption.makeCurrentVersion : null,
      importPreviousVersion: updateOption ? updateOption.importPreviousVersion : null,
      documentSession: this.documentSessionService.getDocumentSession(originalBean.documentId)
    };

    if (this.loggedInUserService.getUserConfigItem('cachingEnabled') === true
      && this.hasOfflineAccess(originalBean) && !this.isAccountDoc(newBean)) {
      return this.indexedDBService.jsonPatchUpdate(newBean, originalBean, options);
    }

    return this.restDataService.jsonPatchUpdate(newBean, options);
  }


  /**
   * Called in CacheLoadService.enable cache to re-arrange primary and secondary data service
   */
  initialise() {

    const _self = this;

    const offlineMode = _self.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
    _self.preferredService = offlineMode ? _self.indexedDBService : _self.restDataService;
    _self.secondaryService = offlineMode ? _self.restDataService : null;

    return _self.initialiseService(_self.preferredService).then(() => {
      return _self.initialiseService(_self.secondaryService);
    });
  }

  // terminateService DataServices on logout. See InitialisationService.terminateDefaultServices
  reset() {

    const _self = this;

    return _self.terminateService(_self.preferredService).then(() => {
      return _self.terminateService(_self.secondaryService);
    }).then(() => {
      // RestDataService is necessary for login.
      // Make RestDataService as the preferred service and reset.
      _self.preferredService = _self.restDataService;
      return _self.initialiseService(_self.preferredService);
    });
  }

  /*
  jsonPatchUpdate function
  parameters:
  bean - the bean to update
  cachingRequired - whether to save the record to the local database. Records that never need
  to be loaded in the app on their own, such as events, can have this set to false
  */
  deepDiffUpdate(originalBean, newBean, options?) {
    return this.jsonPatchUpdate(originalBean, newBean, options);
  }

  /*
   insert function
   parameters:
   bean - the bean to insert
   successFunction - a function to execute on success to do post save steps
   cachingRequired - whether to save the record to the local database. Records that never need
   to be loaded in the app on their own, such as events, can have this set to false
   */
  async insert(bean, cachingRequired?): Promise<any> {
    // set the details like the versionId and previousVersionId
    this.setVersionFields(bean);

    // a different version of the data service will be used depending on whether offline is enabled.
    // assign the service that is to be used rather than the function because assigning a function that is a member of
    // a class means the this object of the function will not be defined
    let dataSaveService;
    let hasOfflineAccess = true;

    try {
      if (bean.systemHeader && bean.systemHeader.templateId) {
        const template = await this.getDocument(bean.systemHeader.templateId);
        hasOfflineAccess = this.hasOfflineAccess(template);
      }

      // always use the Database Service if caching is enabled, so that the items will be placed in the
      // queue to be sent to the server, otherwise items can be saved in the wrong
      // order if there is anything already in the queue
      if (this.loggedInUserService.getUserConfigItem('cachingEnabled') === true
        && hasOfflineAccess && !this.isAccountDoc(bean)) {
        dataSaveService = this.indexedDBService;
      } else {
        dataSaveService = this.restDataService;
      }

      const fullUrl = UtilService.getDataUrl(serverRoutes.document);

      return await dataSaveService.insert(bean, fullUrl, cachingRequired);
    } catch (error) {
      throw error;
    }
  }

  async destroy(id, templateId?, versionId?): Promise<any> {

    const document = await this.getDocument(id);

    let destroyService;

    try {
      if (this.loggedInUserService.getUserConfigItem('cachingEnabled') === true
        && this.hasOfflineAccess(document)) {
        destroyService = this.indexedDBService;
      } else {
        destroyService = this.restDataService;
      }

      return await destroyService.destroy(id, serverRoutes.document, null, templateId, versionId);
    } catch (err) {
      logger.log('Delete failed with error: ' + err);
      throw new Error(err);
    }
  }

  /**
   * load unsynced and cache documents into indexedDB
   * @param isInitialize - whether in the case the documents are cached in the first time
   */
  loadDataCache(isInitialize?): Promise<any> {

    const _self = this;
    if (!_self.loggedInUserService.getUserConfigItem('cachingEnabled') === true || this.isRunningPreviousSync) {
      return Promise.resolve();
    }

    let syncStartTime = _self.offlineStatusService.getLastSyncDate();
    if (!syncStartTime) {
      syncStartTime = 0;
      logger.log('Loading data cache from beginning of time');
    } else {
      logger.log('Loading data cache from time: ' + syncStartTime);
    }

    this.isRunningPreviousSync = true;
    return _self.restDataService.loadDataCache(syncStartTime, isInitialize).then((results) => {
      logger.log('Done caching offline documents. Adding any search indexes from indexedDBIndexes.');

      // registers search indexes
      return _self.indexedDBConnectorService.upgradeIndexedDB().then(
        () => {
          return Promise.resolve(results);
        },
        (err) => {
          return Promise.reject(err);
        }
      );

    }, (err) => {

      logger.error('Error caching offline documents.');
      logger.error(err);

      this.isRunningPreviousSync = false;
      return Promise.reject(err);
    }).then((results) => {

      logger.log('Done registering search indexes.');

      this.isRunningPreviousSync = false;
      return Promise.resolve(results);

    }, (err) => {

      logger.error('Error in registering search indexes.');
      logger.error(err);

      this.isRunningPreviousSync = false;
      return Promise.reject(err);

    });
  }

  clearData() {

    const _self = this;

    if (_self.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {
      return _self.indexedDBService.clearData();
    } else {
      return Promise.reject(new Error('You did not enable caching.'));
    }
  }

  backupData(isFirstTime) {

    const _self = this;

    return new Promise((resolve, reject) => {
      if (_self.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {
        _self.indexedDBService.backupData().then(resolve, reject);
      } else {
        reject(new Error('You did not enable caching.'));
      }
    });
  }

  getIndexedDBIndexes() {
    return this.restDataService.getIndexedDBIndexes();
  }

  async undestroy(id: string): Promise<any> {

    if (this.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {

      const errMsg = 'You need to be online to undelete a document. ';
      logger.error(errMsg);
      throw new Error(errMsg);

    } else {

      const url = 'api/undelete';
      try {
        return await this.restDataService.undestroy(id, url);
      } catch (err) {
        logger.error('Delete failed with error: ' + err);
        throw new Error(err);
      }
    }
  }

  async bulkUploadDocuments(documents): Promise<any> {

    if (this.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {

      const errMsg = 'You need to be online to upload multiple documents. ';
      console.error(errMsg);

      throw new Error(errMsg);

    } else {

      const url = 'api/bulkUploadDocuments';

      try {

        return await this.restDataService.bulkUploadDocuments(documents, url);

      } catch (err) {

        console.error('Upload documents failed with error: ' + err);
        throw new Error(err);

      }
    }
  }
}
